import {
  AddCircleOutline,
  ChevronLeft,
  ChevronRight,
  FilterAlt,
  FilterAltOutlined,
  FormatListBulleted,
  GridView,
  NoteAltOutlined
} from "@mui/icons-material";
import { Box, IconButton, type IconButtonProps, Typography, Tooltip, Stack } from "@mui/material";
import { useUIActions, useUISelector } from "../../../store/UI";
import { useMapActions, useMapSelector } from "../../../store/map";

type DataGridToolbarProps = {
  title: string;
  currentResultCount?: number | string;
  totalResult?: number;
  onPaginationGoBack?: () => void;
  onPaginationGoForward?: () => void;
  onBulkEdit?: () => void;
  onAdd?: () => void;
  isLoadingPagination?: boolean;
  hasPaginationBack?: boolean;
  hasPaginationForward?: boolean;
  showPagination?: boolean;
  showBulkEdit?: boolean;
  showAddOption?: boolean;
  selectedEndpointName?: string;
};

function PaginationDirectionButton({ children, sx, ...restProps }: IconButtonProps) {
  return (
    <IconButton
      {...restProps}
      sx={{
        height: "1.25rem",
        width: "1.25rem",
        color: "#98A2B3",
        fontSize: "1.5",
        aspectRatio: 1,
        "&:hover": {
          backgroundColor: "primary.main"
        },
        "&:disabled": {
          color: "#6c6b6b"
        },
        "& > *": { fontSize: "1.5rem" },
        ...sx
      }}
    >
      {children}
    </IconButton>
  );
}

export default function DataGridToolbar({
  title,
  currentResultCount,
  totalResult,
  onPaginationGoBack,
  onPaginationGoForward,
  onBulkEdit,
  onAdd,
  hasPaginationBack = true,
  hasPaginationForward = true,
  showPagination = true,
  showBulkEdit = true,
  showAddOption = false,
  selectedEndpointName = ""
}: DataGridToolbarProps) {
  const { dataGridViewMode } = useUISelector();
  const { setDataGridViewMode } = useUIActions();
  const { showOnlySelectedEndpointsDataOnMap } = useMapSelector();
  const { setShowOnlySelectedEndpointsDataOnMap } = useMapActions();

  function handleChangeGridViewMode() {
    setDataGridViewMode(dataGridViewMode === "list" ? "grid" : "list");
  }

  return (
    <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", gap: "0.75em" }}>
      <Stack direction='row' gap={"0.5em"} alignItems='center'>
        <Tooltip title={title}>
          <Typography variant='h6' sx={{ color: "#fff" }} className='text-ellipsis'>
            {title}
          </Typography>
        </Tooltip>
        {showAddOption && (
          <Tooltip title={`Add new ${title?.toLowerCase()}`}>
            <IconButton onClick={onAdd} sx={{ px: 2, "& > *": { color: "#FCFCFD" } }}>
              <AddCircleOutline fontSize='medium' />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box sx={{ display: "flex", gap: 16 }}>
        {/* Pagination */}
        {showPagination && (
          <Box sx={{ display: "flex", alignItems: "center", color: "#98A2B3", gap: 4 }}>
            {onPaginationGoBack && (
              <Tooltip title='Go to previous list'>
                <PaginationDirectionButton title='go previous list' onClick={onPaginationGoBack} disabled={!hasPaginationBack}>
                  <ChevronLeft />
                </PaginationDirectionButton>
              </Tooltip>
            )}

            <Typography variant='caption' noWrap>
              {currentResultCount} out of {totalResult}
            </Typography>

            {onPaginationGoForward && (
              <Tooltip title='Go to next list'>
                <PaginationDirectionButton disabled={!hasPaginationForward} onClick={onPaginationGoForward}>
                  <ChevronRight />
                </PaginationDirectionButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Bulk entity selector button*/}
        {showBulkEdit && (
          <Tooltip title={"Edit with bulk editor"}>
            <IconButton onClick={onBulkEdit} sx={{ px: 2, "& > *": { color: "#FCFCFD" } }}>
              <NoteAltOutlined />
            </IconButton>
          </Tooltip>
        )}

        {/* Toggle: Show only selected on map vs Show all */}
        {selectedEndpointName && (
          <Tooltip
            title={showOnlySelectedEndpointsDataOnMap ? "Show all features on map" : `Show only ${selectedEndpointName} results on map`}
          >
            <IconButton
              onClick={() => setShowOnlySelectedEndpointsDataOnMap(!showOnlySelectedEndpointsDataOnMap)}
              sx={{
                px: 2,
                color: showOnlySelectedEndpointsDataOnMap ? "main.primary" : "#FCFCFD",
                "&:hover": {
                  color: showOnlySelectedEndpointsDataOnMap ? "main.primary-hover" : "#D0D5DD"
                }
              }}
            >
              {showOnlySelectedEndpointsDataOnMap ? <FilterAlt /> : <FilterAltOutlined />}
            </IconButton>
          </Tooltip>
        )}

        {/* Grid view toggle button */}
        <Tooltip
          title={
            dataGridViewMode === "list"
              ? "Switch to Card View - Display items as visual cards with thumbnails"
              : "Switch to Table View - Display items in a detailed table format"
          }
        >
          <IconButton onClick={handleChangeGridViewMode} sx={{ px: 2, "& > *": { color: "#FCFCFD" } }}>
            {/* Show the icon of the mode you will switch TO (§6e fix) */}
            {dataGridViewMode === "list" ? <GridView /> : <FormatListBulleted />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
